import * as React from "react";
import { AuthField } from "../AuthField";
import { AuthButton, AuthButtonLink } from "../AuthButton";
import { AuthCardFooter, AuthFooterAction } from "../AuthCardFooter";

type BackAction = { onBack: () => void } | { backTo: string };

type Props = {
  id: string;
  inputRef?: React.Ref<HTMLInputElement>;
  email: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  error?: string | null;
  sending: boolean;
} & BackAction;

function BackToSignIn({ back }: { back: BackAction }) {
  return (
    <AuthCardFooter>
      {"backTo" in back ? (
        <AuthFooterAction to={back.backTo}>Back to Sign In</AuthFooterAction>
      ) : (
        <AuthFooterAction onClick={back.onBack}>
          Back to Sign In
        </AuthFooterAction>
      )}
    </AuthCardFooter>
  );
}

/**
 * Request a password-reset email. Submits the enclosing form. Used in the
 * sign-in card and on /reset-password.
 */
export function ForgotPasswordStep({
  id,
  inputRef,
  email,
  onChange,
  error,
  sending,
  ...back
}: Props) {
  return (
    <>
      <div className="flex flex-col gap-5">
        <AuthField
          ref={inputRef}
          id={id}
          label="Email"
          name="resetEmail"
          type="email"
          inputMode="email"
          autoComplete="new-email"
          placeholder="you@firm.com"
          value={email}
          error={error}
          onChange={onChange}
        />

        <AuthButton
          type="submit"
          fullWidth
          forward
          loading={sending}
          loadingText="Sending…"
        >
          Send Reset Link
        </AuthButton>
      </div>

      <BackToSignIn back={back} />
    </>
  );
}

type SentProps = { onUseDifferentEmail: () => void } & BackAction;

/** After a reset request: back to sign-in, or try another address. */
export function ResetSentStep({ onUseDifferentEmail, ...back }: SentProps) {
  return (
    <>
      {"backTo" in back ? (
        <AuthButtonLink to={back.backTo} fullWidth>
          Back to Sign In
        </AuthButtonLink>
      ) : (
        <AuthButton fullWidth onClick={back.onBack}>
          Back to Sign In
        </AuthButton>
      )}
      <AuthCardFooter lead="Wrong address?">
        <AuthFooterAction onClick={onUseDifferentEmail}>
          Use a Different Email
        </AuthFooterAction>
      </AuthCardFooter>
    </>
  );
}
